import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { resolveAttendancePolicyFor } from "../../../../../../../../services/attendancePolicy";
import { getLegalUnitList, getOrgDepartments } from "../../../../../../../../services/globalData";
import { tokens } from "../../../../../../../../theme";

/**
 * Which attendance policy governs this person, and — when none does — why.
 *
 * "No attendance policy found" stops somebody clocking in, and from the
 * outside a draft that was never published, a policy for another legal entity
 * and a department that merely shares a name all look the same: the admin can
 * see a policy assigned and the employee still cannot punch. The server already
 * decides this; this shows its reasoning instead of making anybody guess.
 *
 * Entities are shown as the resolver sees them — the membership rows — which
 * is not always what the profile's "Legal entity" field says.
 */
const nameOf = (list, id) => (list || []).find((x) => String(x?._id) === String(id))?.name;

const AttendancePolicyFact = ({ employeeId }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    resolveAttendancePolicyFor(employeeId)
      .then((res) => {
        if (!live) return;
        if (res?.success === false) setFailed(true);
        else setData(res?.data || null);
      })
      .catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [employeeId]);

  const label = (
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>
      Attendance policy
    </Box>
  );
  if (failed) return <Box>{label}<Box sx={{ mt: "0.25rem", color: tokens.textMuted }}>Could not check</Box></Box>;
  if (!data) return <Box>{label}<Box sx={{ mt: "0.25rem", color: tokens.textMuted }}>Checking…</Box></Box>;

  if (data.matched) {
    return (
      <Box>
        {label}
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{data.policy?.name}</Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Applies by {data.reason}</Box>
      </Box>
    );
  }

  const units = getLegalUnitList();
  const memberships = (data.employee?.legalUnits || []).map((id) => nameOf(units, id) || "an unknown entity");
  const dept = data.employee?.department;
  return (
    <Box sx={{ gridColumn: "1 / -1" }}>
      {label}
      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.danger, mt: "0.25rem" }}>
        None applies — this person cannot clock in
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.text, mt: "0.5rem", lineHeight: 1.6 }}>
        Decided from: department {dept ? nameOf(getOrgDepartments(), dept) || "not in this entity's list" : "none"}
        {" · "}entity membership {memberships.length ? memberships.join(", ") : "none"}
      </Box>
      {(data.considered || []).length === 0 ? (
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
          The organisation has no attendance policies.
        </Box>
      ) : (
        <Box component="ul" sx={{ m: "0.375rem 0 0", pl: "1.125rem", fontSize: "0.8125rem", color: tokens.text, lineHeight: 1.6 }}>
          {data.considered.map((p) => (
            <li key={p._id}><strong>{p.name}</strong> — {p.skipped}</li>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AttendancePolicyFact;
